import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import Intro from "./Components/Intro";
import Editor from "./Components/Editor";
import Boot from "./Components/Boot";
import { findBuffer } from "./editor/buffers";
import { applyTheme, findTheme, loadThemeName, nextTheme, saveThemeName } from "./editor/themes";
import "./editor.css";

// Routes live in the hash so the site works on static hosting:
//   #/            → intro
//   #/README.md   → editor with that buffer open
function readRoute() {
  const path = decodeURIComponent(window.location.hash.replace(/^#\/?/, ""));
  return path ? findBuffer(path) || findBuffer("README.md") : null;
}

export default function App() {
  const [buffer, setBuffer] = useState(readRoute);
  const [themeName, setThemeName] = useState(loadThemeName);
  const [booting, setBooting] = useState(false);
  const theme = findTheme(themeName);

  // Layout effect so CSS variables are in place before children paint or read them.
  useLayoutEffect(() => {
    applyTheme(theme);
    saveThemeName(theme.name);
  }, [theme]);

  useEffect(() => {
    const onHash = () => setBuffer(readRoute());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    if (!buffer) document.title = "Adeboye Bello — Full-Stack Software Engineer";
  }, [buffer]);

  const open = useCallback((b) => {
    window.location.hash = `/${b.path}`;
  }, []);

  const quit = useCallback(() => {
    window.location.hash = "/";
  }, []);

  const changeTheme = useCallback(
    (name) => {
      const t = name ? findTheme(name) : nextTheme(themeName);
      setThemeName(t.name);
      return t;
    },
    [themeName]
  );

  if (booting) {
    return (
      <Boot
        theme={theme}
        onDone={() => {
          // Set the buffer directly so the intro doesn't flash before hashchange lands.
          const readme = findBuffer("README.md");
          setBuffer(readme);
          setBooting(false);
          open(readme);
        }}
      />
    );
  }

  return buffer ? (
    <Editor buffer={buffer} theme={theme} onOpen={open} onTheme={changeTheme} onQuit={quit} />
  ) : (
    <Intro theme={theme} onEnter={() => setBooting(true)} onTheme={() => changeTheme()} />
  );
}
